/**
 * AdditionCategoryConfig.js
 *
 * Phase 0.3 of the ELM Addition Registry.
 * Defines addition categories (INSERT vs WRAP), their sequencer methods,
 * transferable property allowlists, and default parameters.
 * Provides validateAndMergeParams for safe allowlist-based parameter merging.
 */

const AdditionCategory = Object.freeze({
    INSERT: 'insert',
    WRAP: 'wrap'
});

const ADDITION_CATEGORY_CONFIG = Object.freeze({
    [AdditionCategory.INSERT]: Object.freeze({
        sequencerMethod: 'insertAudioNode',
        transferableProps: Object.freeze([
            { key: 'introFade', type: 'number', defaultValue: 0, min: 0, max: 10000 },
            { key: 'outroFade', type: 'number', defaultValue: 0, min: 0, max: 10000 },
            { key: 'volume', type: 'number', defaultValue: 100, min: 0, max: 100 },
            { key: 'introPadding', type: 'number', defaultValue: 0, min: 0, max: 10000 },
            { key: 'outroPadding', type: 'number', defaultValue: 0, min: 0, max: 10000 }
        ]),
        defaultParams: Object.freeze({
            insertBefore: false,
            introPadding: 0,
            outroPadding: 0,
            introFade: 0,
            outroFade: 0,
            volume: 100
        })
    }),
    [AdditionCategory.WRAP]: Object.freeze({
        sequencerMethod: 'wrapSelectedNodesWithMusic',
        transferableProps: Object.freeze([
            { key: 'introFade', type: 'number', defaultValue: 2000, min: 0, max: 10000 },
            { key: 'outroFade', type: 'number', defaultValue: 2000, min: 0, max: 10000 },
            { key: 'volume', type: 'number', defaultValue: 30, min: 0, max: 100 },
            { key: 'loop', type: 'boolean', defaultValue: true },
            { key: 'ducking', type: 'boolean', defaultValue: true },
            { key: 'introPadding', type: 'number', defaultValue: 0, min: 0, max: 10000 },
            { key: 'outroPadding', type: 'number', defaultValue: 0, min: 0, max: 10000 }
        ]),
        defaultParams: Object.freeze({
            introPadding: 0,
            outroPadding: 0,
            introFade: 2000,
            outroFade: 2000,
            volume: 30,
            loop: true,
            ducking: true
        })
    })
});

/**
 * Validates incoming parameters against an allowlist of transferable properties,
 * merging valid values onto a shallow copy of defaultParams.
 * Any key not in the allowlist is dropped.
 *
 * @param {Array<{key: string, type: string, defaultValue: *, min?: number, max?: number}>} transferableProps
 * @param {Object} defaultParams
 * @param {Object} incomingParams
 * @returns {Object} Merged parameters with only allowlisted, validated keys
 */
function validateAndMergeParams(transferableProps, defaultParams, incomingParams) {
    const merged = Object.assign({}, defaultParams);

    if (!incomingParams || !transferableProps) {
        return merged;
    }

    for (let i = 0; i < transferableProps.length; i++) {
        const prop = transferableProps[i];
        const key = prop.key;

        if (!(key in incomingParams)) {
            continue;
        }

        const value = incomingParams[key];

        if (prop.type === 'number') {
            if (typeof value !== 'number' || isNaN(value)) {
                continue;
            }
            if (prop.min !== undefined && value < prop.min) {
                continue;
            }
            if (prop.max !== undefined && value > prop.max) {
                continue;
            }
            merged[key] = value;
        } else if (prop.type === 'boolean') {
            if (typeof value !== 'boolean') {
                continue;
            }
            merged[key] = value;
        }
    }

    return merged;
}

export default {
    AdditionCategory: AdditionCategory,
    ADDITION_CATEGORY_CONFIG: ADDITION_CATEGORY_CONFIG,
    validateAndMergeParams: validateAndMergeParams
};
